import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { ContactInfoEntry, ContactType, CoreContactType, ExtendedContactType } from 'simple-creator/types';

interface ContactInfoFormProps {
  contacts: ContactInfoEntry[];
  onChange: (contacts: ContactInfoEntry[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  coreTypes?: CoreContactType[];
  extendedTypes?: ExtendedContactType[];
  className?: string;
}

const CONTACT_TYPE_CONFIG: Record<ContactType, { label: string; placeholder: string; inputType: string }> = {
  email: { label: 'Email', placeholder: 'email@example.com', inputType: 'email' },
  phone: { label: 'Phone', placeholder: '+43 1234 5678', inputType: 'tel' },
  website: { label: 'Website', placeholder: 'https://example.com', inputType: 'url' },
  name: { label: 'Contact Person', placeholder: 'John Doe', inputType: 'text' },
  landline: { label: 'Landline', placeholder: '+43 1234 5678', inputType: 'tel' },
  ticket: { label: 'Ticket Info', placeholder: 'https://tickets.example.com', inputType: 'url' },
  'ticket-print-info': { label: 'Ticket Print Info', placeholder: 'Tickets available at...', inputType: 'text' },
};

const PHONE_REGEX = /^[+]?[0-9]{0,3}\W?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HTTPS_URL_REGEX = /^https:\/\/[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+\S*$/i;

const validateField = (type: ContactType, value: string): string | null => {
  if (!value) return null; // Empty is valid (optional fields)
  
  switch (type) {
    case 'phone':
    case 'landline':
      return PHONE_REGEX.test(value) ? null : 'Please enter a valid phone number';
    case 'email':
      return EMAIL_REGEX.test(value) ? null : 'Please enter a valid email address';
    case 'website':
      return HTTPS_URL_REGEX.test(value) ? null : 'Please enter a valid URL (https://example.com)';
    case 'ticket':
      return HTTPS_URL_REGEX.test(value) ? null : 'Please enter a valid URL (https://tickets.example.com)';
    default:
      return null;
  }
};

const DEFAULT_CORE_TYPES: CoreContactType[] = ['phone', 'email', 'website'];
const DEFAULT_EXTENDED_TYPES: ExtendedContactType[] = ['name', 'landline', 'ticket', 'ticket-print-info'];

export const ContactInfoForm: React.FC<ContactInfoFormProps> = ({
  contacts,
  onChange,
  onValidationChange,
  coreTypes = DEFAULT_CORE_TYPES,
  extendedTypes = DEFAULT_EXTENDED_TYPES,
  className = '',
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<ContactType>>(new Set());

  const getContactByType = useCallback((type: ContactType): ContactInfoEntry | undefined => {
    return contacts.find((c) => c.type === type);
  }, [contacts]);

  const getValidationError = (type: ContactType): string | null => {
    const contact = getContactByType(type);
    if (!touchedFields.has(type)) return null;
    return validateField(type, contact?.content || '');
  };

  const checkAllFieldsValid = useCallback((): boolean => {
    for (const contact of contacts) {
      const error = validateField(contact.type, contact.content);
      if (error) return false;
    }
    return true;
  }, [contacts]);

  const prevIsValidRef = useRef<boolean | null>(null);
  
  useEffect(() => {
    if (onValidationChange) {
      const isValid = checkAllFieldsValid();
      // Only call onValidationChange if the validation state actually changed
      if (prevIsValidRef.current !== isValid) {
        prevIsValidRef.current = isValid;
        onValidationChange(isValid);
      }
    }
  }, [contacts, onValidationChange, checkAllFieldsValid]);

  useEffect(() => {
    const fieldsWithContent = contacts
      .filter((c) => c.content && c.content.trim() !== '')
      .map((c) => c.type);
    if (fieldsWithContent.length > 0) {
      setTouchedFields((prev) => {
        const next = new Set(prev);
        fieldsWithContent.forEach((t) => next.add(t));
        return next;
      });
    }
  }, [contacts]);

  const handleBlur = (type: ContactType) => {
    setTouchedFields((prev) => new Set(prev).add(type));
  };

  const updateContact = (type: ContactType, content: string) => {
    const existing = contacts.find((c) => c.type === type);
    if (existing) {
      if (content) {
        onChange(contacts.map((c) => (c.type === type ? { ...c, content } : c)));
      } else {
        onChange(contacts.filter((c) => c.type !== type));
      }
    } else if (content) {
      onChange([...contacts, { type, content }]);
    }
  };

  const addExtendedContact = (type: ExtendedContactType) => {
    if (!contacts.find((c) => c.type === type)) {
      onChange([...contacts, { type, content: '' }]);
    }
    setShowDropdown(false);
  };

  const removeContact = (type: ContactType) => {
    onChange(contacts.filter((c) => c.type !== type));
  };

  const addedExtendedTypes = contacts
    .map((c) => c.type)
    .filter((t): t is ExtendedContactType => extendedTypes.includes(t as ExtendedContactType));

  const availableExtendedTypes = extendedTypes.filter(
    (t) => !addedExtendedTypes.includes(t)
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Core contact fields - always visible */}
      <div className="space-y-3">
        {coreTypes.map((type) => {
          const config = CONTACT_TYPE_CONFIG[type];
          const contact = getContactByType(type);
          const error = getValidationError(type);
          return (
            <div key={type}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {config.label}
              </label>
              <input
                type={config.inputType}
                value={contact?.content || ''}
                onChange={(e) => updateContact(type, e.target.value)}
                onBlur={() => handleBlur(type)}
                placeholder={config.placeholder}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors ${
                  error 
                    ? 'border-red-400 focus:ring-red-100 focus:border-red-400' 
                    : 'border-gray-300 focus:ring-balloon/20 focus:border-balloon'
                }`}
              />
              {error && (
                <p className="mt-1 text-xs text-red-500">{error}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Added extended contact fields */}
      {addedExtendedTypes.length > 0 && (
        <div className="border-t border-gray-200 pt-4 mt-4 space-y-3">
          {addedExtendedTypes.map((type) => {
            const config = CONTACT_TYPE_CONFIG[type];
            const contact = getContactByType(type);
            const error = getValidationError(type);
            return (
              <div key={type}>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700">
                    {config.label}
                  </label>
                  <button
                    type="button"
                    onClick={() => removeContact(type)}
                    className="text-gray-400 hover:text-red-500 text-xs flex items-center gap-1"
                    aria-label={`Remove ${config.label}`}
                  >
                    <span>✕</span> Remove
                  </button>
                </div>
                <input
                  type={config.inputType}
                  value={contact?.content || ''}
                  onChange={(e) => updateContact(type, e.target.value)}
                  onBlur={() => handleBlur(type)}
                  placeholder={config.placeholder}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors ${
                    error 
                      ? 'border-red-400 focus:ring-red-100 focus:border-red-400' 
                      : 'border-gray-300 focus:ring-balloon/20 focus:border-balloon'
                  }`}
                />
                {error && (
                  <p className="mt-1 text-xs text-red-500">{error}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add more dropdown */}
      {availableExtendedTypes.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full py-2.5 text-balloon border-2 border-dashed border-balloon/50 hover:border-balloon hover:bg-balloon/5 rounded-xl transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <span>+</span>
            <span>Add more contact info...</span>
          </button>

          {showDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
              {availableExtendedTypes.map((type) => {
                const config = CONTACT_TYPE_CONFIG[type];
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addExtendedContact(type)}
                    className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm"
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
